import { useState } from 'react'
import { Download, Plus, Upload, Users } from 'lucide-react'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AdminListPage } from '../components/AdminListPage'
import { MailingListFiltersBar } from '../components/mailingList/MailingListFiltersBar'
import { AddMemberDialog } from '../components/mailingList/AddMemberDialog'
import { BulkAddDialog } from '../components/mailingList/BulkAddDialog'
import { CsvImportDialog } from '../components/mailingList/CsvImportDialog'
import { useAdminMailingList } from '../hooks/useAdminMailingList'
import { generateCsv, downloadCsv, type ParsedEmailEntry } from '@/lib/csv'
import type { MailingListMemberDocument } from '@/types'
import type { MailingListMemberFormValues } from '@/features/mailing-list'

const SEGMENT_LABELS: Record<string, string> = {
  general: 'General',
  'film-updates': 'Film Updates',
  services: 'Services',
  oddfest: 'ODDFEST',
  shift: 'S.H.I.F.T.',
}

const SOURCE_LABELS: Record<string, string> = {
  'footer-signup': 'Footer',
  'service-request': 'Request',
  'admin-manual': 'Manual',
  'admin-bulk': 'Bulk',
  'admin-csv': 'CSV',
}

function formatDate(value: unknown): string {
  if (!value) return '—'
  try {
    // Handle Firestore Timestamp objects
    const date =
      value instanceof Date
        ? value
        : typeof value === 'object' && value !== null && 'toDate' in value
          ? (value as { toDate(): Date }).toDate()
          : new Date(String(value))
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return '—'
  }
}

export function AdminMailingListPage() {
  const { members, filtered, loading, error, filters, setFilters, addMember, bulkAdd, deleteMember } =
    useAdminMailingList()

  const [addOpen, setAddOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [csvOpen, setCsvOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<(MailingListMemberDocument & { id: string }) | null>(null)
  const [deleting, setDeleting] = useState(false)

  function handleDownloadCsv() {
    const csv = generateCsv(filtered, [
      { label: 'Email', getValue: (r) => r.email },
      { label: 'First Name', getValue: (r) => r.firstName ?? '' },
      { label: 'Last Name', getValue: (r) => r.lastName ?? '' },
      { label: 'Segments', getValue: (r) => r.segments.join(';') },
      { label: 'Source', getValue: (r) => r.source },
      { label: 'Subscribed', getValue: (r) => (r.isSubscribed ? 'Yes' : 'No') },
      { label: 'Joined', getValue: (r) => formatDate(r.createdAt) },
    ])
    const date = new Date().toISOString().slice(0, 10)
    downloadCsv(csv, `mailing-list-${date}.csv`)
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteMember(deleteTarget.id)
    } finally {
      setDeleting(false)
      setDeleteTarget(null)
    }
  }

  async function handleBulkAdd(entries: ParsedEmailEntry[]) {
    return bulkAdd(entries, 'admin-bulk')
  }

  async function handleCsvAdd(entries: ParsedEmailEntry[]) {
    return bulkAdd(entries, 'admin-csv')
  }

  return (
    <>
      <AdminListPage
        title="Mailing List"
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadCsv}
              disabled={filtered.length === 0}
            >
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Download CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCsvOpen(true)}>
              <Upload className="mr-1.5 h-3.5 w-3.5" />
              Import CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}>
              <Users className="mr-1.5 h-3.5 w-3.5" />
              Bulk Add
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add Member
            </Button>
          </div>
        }
        filters={
          <MailingListFiltersBar
            filters={filters}
            onChange={setFilters}
            totalCount={members.length}
            filteredCount={filtered.length}
          />
        }
        error={error}
        loading={loading}
        skeletonRows={6}
        dialog={
          <>
            <AddMemberDialog
              open={addOpen}
              onOpenChange={setAddOpen}
              onAdd={async (data: MailingListMemberFormValues) => {
                await addMember(data)
              }}
            />
            <BulkAddDialog
              open={bulkOpen}
              onOpenChange={setBulkOpen}
              onBulkAdd={handleBulkAdd}
            />
            <CsvImportDialog
              open={csvOpen}
              onOpenChange={setCsvOpen}
              onBulkAdd={handleCsvAdd}
            />
            <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Remove Member</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to remove{' '}
                  <span className="font-semibold text-foreground">
                    {deleteTarget?.email}
                  </span>{' '}
                  from the mailing list? This action cannot be undone.
                </p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDeleteTarget(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => void handleDelete()}
                    disabled={deleting}
                  >
                    {deleting ? 'Removing…' : 'Remove'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Segments</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                  {members.length === 0
                    ? 'No members yet. Add one to get started.'
                    : 'No members match the current filters.'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-mono text-sm">{member.email}</TableCell>
                  <TableCell>
                    {[member.firstName, member.lastName].filter(Boolean).join(' ') || (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {member.segments.map((seg) => (
                        <Badge key={seg} variant="secondary" className="text-xs">
                          {SEGMENT_LABELS[seg] ?? seg}
                        </Badge>
                      ))}
                      {member.segments.length === 0 && (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {SOURCE_LABELS[member.source] ?? member.source}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={member.isSubscribed ? 'default' : 'secondary'}>
                      {member.isSubscribed ? 'Subscribed' : 'Unsubscribed'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(member.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(member)}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Remove</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </AdminListPage>
    </>
  )
}
